import { useTellescopeQuery } from "@tellescope/tanstack-query";

// Stripe subscription types based on the proxy_read response
export interface StripeProduct {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  metadata?: Record<string, string>;
  images?: string[];
}

export interface StripePlan {
  id: string;
  active: boolean;
  amount: number; // Amount in cents
  currency: string;
  interval: "day" | "week" | "month" | "year";
  interval_count: number;
  // Stripe expands product on top-level plan but returns a string ID on item-level plans
  product: StripeProduct | string;
}

/**
 * Returns the product name from a plan, handling both expanded product objects
 * (returned for single-item subscriptions) and unexpanded string IDs (returned
 * for items inside multi-item subscriptions).
 */
export function getStripePlanProductName(plan: StripePlan | null): string | null {
  if (!plan) return null;
  if (typeof plan.product === "object") return plan.product.name ?? null;
  // product is a string ID — Stripe didn't expand it; no customer-facing name available
  return null;
}

/**
 * Returns the product image from a plan, handling both expanded and unexpanded product.
 */
export function getStripePlanProductImage(plan: StripePlan | null): string | undefined {
  if (!plan || typeof plan.product !== "object") return undefined;
  return plan.product.images?.[0];
}

export interface StripePaymentMethod {
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
}

export interface SubscriptionDiscountInfo {
  id: string;
  name: string | null;
  duration: "forever" | "once" | "repeating";
  durationInMonths: number | null; // only meaningful when duration === "repeating"
  end: number | null; // Unix seconds; populated only for "repeating" duration
  percentOff: number | null;
  amountOff: number | null;
  appliesToProductIds: string[]; // [] means unrestricted (applies to all products)
  promotionCode: string | null;
}

export interface SubscriptionPreviewLineItem {
  subscriptionItemId: string | null;
  description: string | null;
  amount: number; // net amount after discounts, smallest currency unit
  discountAmounts: { discountId: string; amount: number }[];
  period: { start: number; end: number };
}

export interface SubscriptionPricePreview {
  currency: string;
  subtotal: number;
  total: number;
  amountDue: number;
  totalDiscountAmount: number;
  periodStart: number;
  periodEnd: number;
  discounts: SubscriptionDiscountInfo[];
  lines: SubscriptionPreviewLineItem[];
}

interface StripeSubscriptionRaw {
  id: string;
  status: "active" | "past_due" | "unpaid" | "canceled" | "incomplete" | "incomplete_expired" | "trialing" | "paused";
  current_period_start: number; // Unix timestamp
  current_period_end: number; // Unix timestamp
  cancel_at_period_end: boolean;
  canceled_at?: number;
  ended_at?: number;
  plan: StripePlan | null;
  items?: { data: { id: string; plan: StripePlan }[] };
  default_payment_method?: StripePaymentMethod;
  pause_collection?: {
    behavior: "keep_as_draft" | "mark_uncollectible" | "void";
    resumes_at?: number | null;
  } | null;
  schedule?: string | null;
  trial_end?: number | null;
  pricePreview?: SubscriptionPricePreview | null;
}

export interface StripeSubscription extends StripeSubscriptionRaw {
  // For single-item subscriptions: the top-level plan (with expanded product).
  // For multi-item subscriptions: null (use resolvedItems instead).
  effectivePlan: StripePlan | null;
  // All resolved item plans. Single-item subscriptions have one entry;
  // multi-item subscriptions have one entry per item.
  resolvedItems: StripePlan[];
}

/**
 * Helper to check if a subscription has paused billing.
 * Stripe's pause_collection feature keeps status as "active" but sets pause_collection.
 */
export function isSubscriptionPaused(subscription: StripeSubscription): boolean {
  return subscription.pause_collection != null || subscription.status === "paused";
}

/**
 * Returns the Unix timestamp (seconds) billing resumes at, if known.
 * Only set when pause_collection.resumes_at is present — indefinite pauses have no resume date.
 */
export function getSubscriptionResumesAt(subscription: StripeSubscription): number | null {
  return subscription.pause_collection?.resumes_at ?? null;
}

export interface UseStripeSubscriptionsOptions {
  limit?: number;
}

export function useStripeSubscriptions(options: UseStripeSubscriptionsOptions = {}) {
  const { limit = 20 } = options;

  const query = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Stripe",
      type: "subscriptions",
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - same as default
    select: (response) => {
      const raw = (response?.data as { subscriptions: StripeSubscriptionRaw[] }) ?? { subscriptions: [] };
      return {
        subscriptions: raw.subscriptions.map((s) => ({
          ...s,
          effectivePlan: s.plan ?? null,
          resolvedItems: s.plan ? [s.plan] : s.items?.data?.map((item) => item.plan) ?? [],
        })),
      };
    },
  });

  // Extract and limit subscriptions
  const allSubscriptions = Array.isArray(query.data?.subscriptions) ? query.data.subscriptions : [];
  const subscriptions = allSubscriptions.slice(0, limit);

  return {
    ...query,
    subscriptions,
  };
}
